import React from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Check, Sparkles } from 'lucide-react';

export const PricingPage: React.FC = () => {
  const tiers = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Ideal for small teams and fast-moving startups getting started.',
      features: [
        'Unlimited public & private channels',
        '10,000 recent message history',
        '1-to-1 Direct Messaging',
        'Standard STOMP Realtime sync',
        'Web, Windows & Android apps',
        'Community support',
      ],
      cta: 'Start for Free',
      highlighted: false,
    },
    {
      name: 'Pro',
      price: '$8',
      period: 'per user / month',
      description: 'For growing teams that need unlimited history, AI agent summaries, and priority sync.',
      features: [
        'Unlimited message history & search',
        'Unlimited channel groups & threads',
        'Embedded SahajBot AI summaries',
        'Custom emoji reactions',
        'Desktop native notifications',
        'Priority WebSocket broker connection',
        '99.9% uptime SLA with priority support',
      ],
      cta: 'Get Started with Pro',
      highlighted: true,
    },
    {
      name: 'Enterprise',
      price: '$18',
      period: 'per user / month',
      description: 'Advanced security, tenant isolation, and custom compliance controls.',
      features: [
        'Dedicated PostgreSQL cluster',
        'Custom SSO (SAML 2.0 / OIDC)',
        'Custom token expiry policies',
        'Audit logs & compliance exports',
        'Enterprise device management',
        '24/7 dedicated support engineer',
      ],
      cta: 'Contact Sales',
      highlighted: false,
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F9FB] text-[#1a1b4b] antialiased selection:bg-[#3a9efd]/20 selection:text-[#1a1b4b]">
      <Navbar />
      <main className="flex-1 pt-28 pb-20">
        <div className="container mx-auto max-w-7xl px-4 sm:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#292a73]/10 border border-[#3e4491]/20 text-[#292a73] text-xs font-bold">
              <Sparkles className="h-3.5 w-3.5 text-[#f7a400]" />
              <span>Transparent Plans</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-[#1a1b4b]">
              Simple,{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#292a73] via-[#3a9efd] to-[#f7a400]">
                transparent pricing.
              </span>
            </h1>
            <p className="text-base text-[#3e4491] font-medium leading-relaxed max-w-2xl mx-auto">
              Choose the plan that matches your team's velocity. Upgrade or downgrade anytime.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tiers.map((tier, i) => (
              <div
                key={i}
                className={`p-8 rounded-3xl border-2 flex flex-col justify-between transition-all ${
                  tier.highlighted
                    ? 'border-[#3a9efd] bg-white shadow-2xl shadow-[#292a73]/15 ring-2 ring-[#3a9efd]/25 relative transform md:-translate-y-2'
                    : 'border-[#3e4491]/20 bg-white shadow-sm hover:border-[#3a9efd]/60'
                }`}
              >
                {tier.highlighted && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-[#f7a400] text-[#1a1b4b] px-3.5 py-1 rounded-full text-xs font-black shadow-md">
                    <Sparkles className="h-3 w-3" /> Most Popular
                  </div>
                )}

                <div>
                  <h3 className="font-bold text-2xl text-[#1a1b4b] mb-2">{tier.name}</h3>
                  <p className="text-xs text-[#3e4491] font-medium mb-6 min-h-[32px]">{tier.description}</p>

                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-5xl font-black text-[#1a1b4b]">{tier.price}</span>
                    <span className="text-xs text-[#3e4491] font-semibold">/{tier.period}</span>
                  </div>

                  <ul className="space-y-3.5 text-xs mb-8">
                    {tier.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-[#1a1b4b] font-medium">
                        <Check className="h-4 w-4 text-[#3a9efd] shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link
                  to="/register"
                  className={`w-full py-3.5 rounded-xl font-bold text-sm text-center transition-all ${
                    tier.highlighted
                      ? 'bg-[#1a1b4b] hover:bg-[#292a73] text-white shadow-md shadow-[#1a1b4b]/25 hover:shadow-lg hover:-translate-y-0.5'
                      : 'bg-[#1a1b4b]/10 hover:bg-[#1a1b4b] text-[#1a1b4b] hover:text-white'
                  }`}
                >
                  {tier.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
